// Generic Approval Integration Component
// Provides approval functionality for any entity (documents, revisions, CAPAs, etc.)

import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  PlusIcon,
  ClockIcon,
  EyeIcon,
  TrashIcon,
  ConfirmationDialog,
  AlertDialog
} from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { useEntityApprovals, useCreateApprovalRequest, useUpdateApprovalRequest, useDeleteApprovalRequest } from '../../modules/approval/hooks/useApprovalRequests'; 
import ApprovalIntegration from '../../modules/approval/components/ApprovalIntegration';
import { getEntityLabels } from '../generic/labels';
import ApprovalEntitySummary from './ApprovalEntitySummary';

const GenericApprovalIntegration = ({ 
  entity, 
  entityType,
  entityData,
  document, // Document object (for revisions - to get approvalTemplateId)
  onApprovalUpdate,
  showFullIntegration = false,
  config = {},
  // Form integration props
  formMode = 'standalone', // 'standalone', 'inline', 'modal', 'drawer'
  showInForm = true,
  onFormSubmit,
  onFormCancel,
  // Approval specific props
  approvalConfig = {},
  // Entity specific props
  entityConfig = {},
  // Custom props
  customTitle,
  customDescription,
  customIcon,
  // Additional props
  ...props
}) => {
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [editFormState, setEditFormState] = useState({ show: false, approval: null, viewOnly: false });
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmState, setDeleteConfirmState] = useState({ show: false, approval: null });
  const [alertState, setAlertState] = useState({ show: false, message: '', type: 'error' });
  const { user } = useAuth();

  // Ensure only one modal is open at a time
  useEffect(() => {
    if (editFormState.show) {
      setShowApprovalForm(false);
    }
  }, [editFormState.show]);

  useEffect(() => {
    if (showApprovalForm) {
      setEditFormState({ show: false, approval: null });
    }
  }, [showApprovalForm]);

  // Merge configurations
  const defaultConfig = {
    title: 'Document Review and Approval Status',
    noApprovalMessage: 'No review and approval request has been submitted',
    approvalButtonText: 'Request Review and Approval',
    ...config,
    ...approvalConfig,
    ...(customTitle ? { title: customTitle } : {}),
  };

  // Fetch data using SWR hooks
  const { approvals: approvalRequests, isLoading: approvalLoading, mutate: refreshApprovals } = useEntityApprovals(entityType, entity?.id);

  // Debug: Log the approval requests data structure
  useEffect(() => {
    if (approvalRequests && approvalRequests.length > 0) {
      console.log('Approval requests data structure:', approvalRequests[0]);
      console.log('Current user:', user);
      console.log('Can delete check:', {
        approval: approvalRequests[0],
        user: user,
        requestedById: approvalRequests[0].requestedById,
        requestedBy: approvalRequests[0].requestedBy,
        status: approvalRequests[0].status,
        canDelete: canDeleteApproval(approvalRequests[0]),
        canEdit: canEditApproval(approvalRequests[0])
      });
    }
  }, [approvalRequests, user]);

  // SWR mutation hooks
  const createApprovalRequest = useCreateApprovalRequest();
  const updateApprovalRequest = useUpdateApprovalRequest();
  const deleteApprovalRequest = useDeleteApprovalRequest();

  // Check for existing pending approvals
  useEffect(() => {
    if (approvalRequests && approvalRequests.length > 0) {
      const hasPendingApprovals = approvalRequests.some(approval => {
        const statusValue = typeof approval.status === 'string' ? approval.status : (approval.status?.name || approval.status);
        return statusValue === 'pending' || statusValue === 'overdue';
      });
      if (hasPendingApprovals && onApprovalUpdate) {
        onApprovalUpdate(hasPendingApprovals);
      }
    }
  }, [approvalRequests, onApprovalUpdate]);

  const handleApprovalRequest = async (approvalData) => {
    try {
      setIsLoading(true);
      const result = await createApprovalRequest(approvalData);
      if (result) {
        setShowApprovalForm(false);
        refreshApprovals();
        if (onApprovalUpdate) {
          onApprovalUpdate(true);
        }
      }
    } catch (error) {
      console.error('Error creating approval request:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditApprovalRequest = async (approvalData) => {
    try {
      setIsLoading(true);
      const result = await updateApprovalRequest(editFormState.approval.id, approvalData);
      if (result) {
        setEditFormState({ show: false, approval: null });
        refreshApprovals();
        if (onApprovalUpdate) {
          onApprovalUpdate(true);
        }
      }
    } catch (error) {
      console.error('Error updating approval request:', error);
      // Show error message in alert dialog
      const errorMessage = error.message || 'Failed to update approval request. Please try again.';
      setAlertState({
        show: true,
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if any actions have been done by OTHER users (not the current user)
  const hasActionsByOtherUsers = (approval) => {
    if (!approval || !approval.steps || approval.steps.length === 0) return false;
    if (!user || !user.id) return false;
    
    const currentUserId = user.id;
    
    // Check if any step has been approved, reviewed, or rejected by someone other than the requester
    return approval.steps.some(step => {
      // Check if step has been approved by someone other than the requester
      if (step.approvedBy && step.approvedBy.id && step.approvedBy.id !== approval.requestedById) {
        return true;
      }
      // Check if step has been reviewed by someone other than the requester
      if (step.reviewedBy && step.reviewedBy.id && step.reviewedBy.id !== approval.requestedById) {
        return true;
      }
      // Check if step has been rejected by someone other than the requester
      if (step.rejectedBy && step.rejectedBy.id && step.rejectedBy.id !== approval.requestedById) {
        return true;
      }
      // Check if step has approval/review/rejection dates (indicating action taken)
      if (step.approvedAt || step.reviewedAt || step.rejectedAt) {
        // If the step has an assigned approver, check if they're different from requester
        if (step.assignedApprover && step.assignedApprover.id && step.assignedApprover.id !== approval.requestedById) {
          return true;
        }
      }
      return false;
    });
  };

  const canEditApproval = (approval) => {
    if (!approval) return false;
    
    // Must be created by the logged-in user
    if (!user || !user.id) return false;
    
    // Get requestedById from either direct field or relation object
    const requestedById = approval.requestedById || approval.requestedBy?.id;
    if (!requestedById) return false;
    
    // Convert both to numbers for comparison to handle type mismatches (string vs number)
    const currentUserId = Number(user.id);
    const approvalRequestedById = Number(requestedById);
    
    // User must be the creator
    if (approvalRequestedById !== currentUserId) {
      return false;
    }
    
    // Check statusId: null, undefined, or 1 means pending (default state)
    // If statusId is set and not 1, it's not pending
    const statusId = approval.statusId;
    const isPendingById = (statusId === null || statusId === undefined || statusId === 1);
    
    if (!isPendingById) {
      return false;
    }
    
    // Handle both string and object status
    // If statusId is null/undefined/1, check if status object exists
    let statusValue = null;
    if (approval.status) {
      statusValue = typeof approval.status === 'string' ? approval.status : (approval.status?.name || approval.status);
    }
    
    // If status value is set and not pending, reject
    if (statusValue && statusValue.toLowerCase() !== 'pending') {
      return false;
    }
    
    // Cannot edit if other users have taken actions
    if (hasActionsByOtherUsers(approval)) return false;
    
    return true;
  };

  const canDeleteApproval = (approval) => {
    if (!approval) {
      // console.log('canDeleteApproval: No approval object');
      return false;
    }
    
    // Must be created by the logged-in user
    if (!user || !user.id) {
      // console.log('canDeleteApproval: No user or user.id', { user });
      return false;
    }
    
    // Get requestedById from either direct field or relation object
    const requestedById = approval.requestedById || approval.requestedBy?.id;
    if (!requestedById) {
      // console.log('canDeleteApproval: No requestedById found', { 
      //   approval: { 
      //     id: approval.id, 
      //     requestedById: approval.requestedById, 
      //     requestedBy: approval.requestedBy 
      //   } 
      // });
      return false;
    }
    
    // Convert both to numbers for comparison to handle type mismatches (string vs number)
    const currentUserId = Number(user.id);
    const approvalRequestedById = Number(requestedById);
    
    // User must be the creator
    if (approvalRequestedById !== currentUserId) {
      // console.log('canDeleteApproval: User mismatch', { 
      //   currentUserId, 
      //   approvalRequestedById, 
      //   requestedById,
      //   userObject: user
      // });
      return false;
    }
    
    // Handle both string and object status
    // If statusId is null, check if status object exists or default to 'pending'
    let statusValue = null;
    if (approval.status) {
      statusValue = typeof approval.status === 'string' ? approval.status : (approval.status?.name || approval.status);
    } else if (approval.statusId === null || approval.statusId === undefined) {
      // If statusId is null/undefined, it likely means pending (default state)
      statusValue = 'pending';
    }
    
    if (statusValue && statusValue !== 'pending' && statusValue !== 'Pending') {
      // console.log('canDeleteApproval: Status is not pending', { statusValue, statusId: approval.statusId, status: approval.status });
      return false;
    }
    
    // Cannot delete if other users have taken actions
    if (hasActionsByOtherUsers(approval)) {
      // console.log('canDeleteApproval: Other users have taken actions');
      return false;
    }
    
    // console.log('canDeleteApproval: All checks passed - CAN DELETE', { approvalId: approval.id });
    return true;
  };

  const handleDeleteApproval = (approval) => {
    if (!approval || !approval.id) return;
    setDeleteConfirmState({ show: true, approval });
  };

  const confirmDeleteApproval = async () => {
    const { approval } = deleteConfirmState;
    if (!approval || !approval.id) return;
    
    try {
      setIsLoading(true);
      await deleteApprovalRequest(approval.id);
      refreshApprovals();
      if (onApprovalUpdate) {
        onApprovalUpdate(false);
      }
      setDeleteConfirmState({ show: false, approval: null });
    } catch (error) {
      console.error('Error deleting approval request:', error);
      setAlertState({ 
        show: true, 
        message: `Failed to delete approval request: ${error.message}`, 
        type: 'error' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprovalAction = (action, { approvalId }) => {
    // console.log('handleApprovalAction called with:', { action, approvalId });
    const approval = approvalRequests.find(a => a.id === approvalId);
    // console.log('Found approval:', approval);
    
    if (!approval) return;
    
    if (action === 'view') {
      // Open in view-only mode
      setEditFormState({ show: true, approval, viewOnly: true });
    } else if (action === 'edit' && canEditApproval(approval)) {
      // Open in edit mode
      setEditFormState({ show: true, approval, viewOnly: false });
    } else if (action === 'delete' && canDeleteApproval(approval)) {
      // Delete the approval
      handleDeleteApproval(approval);
    }
  };

  const getStatusText = (status) => {
    // Handle both string and object status
    const statusValue = typeof status === 'string' ? status : (status?.name || status);
    
    const statusMap = {
      'Pending': 'Pending',
      'Approved': 'Approved',
      'Rejected': 'Rejected',
      'Cancelled': 'Cancelled',
      'Overdue': 'Overdue'
    };
    
    const statusText = statusMap[statusValue] || statusValue;
    const colorMap = {
      'Pending': 'text-yellow-600',
      'Approved': 'text-green-600',
      'Rejected': 'text-red-600',
      'Cancelled': 'text-gray-600',
      'Overdue': 'text-red-800'
    };
    
    return (
      <span className={`text-xs font-medium ${colorMap[statusValue] || 'text-gray-600'}`}>
        {statusText}
      </span>
    );
  };

  const getApproverInfo = (approval) => {
    const approvers = [];
    
    if (approval.steps && approval.steps.length > 0) {
      approval.steps.forEach(step => {
        if (step.assignedApprover) {
          const approverType = step.approverType === 'reviewer' ? 'Reviewer' : 'Approver';
          
          approvers.push({
            name: step.assignedApprover.userDisplayName || step.assignedApprover.userFirstName || 'Unknown',
            type: approverType,
            date: step.completedAt,
            isAssigned: step.isAssigned,
            stepName: step.stepName,
            status: step.status?.name || step.status || 'pending'
          });
          
          // console.log('Step debug:', {
          //   stepId: step.id,
          //   stepStatus: step.status,
          //   stepStatusName: step.status?.name,
          //   finalStatus: step.status?.name || step.status || 'pending'
          // });
        }
      });
    }

    if (approvers.length === 0) {
      return <div className="text-xs text-gray-500">No approvers assigned</div>;
    }

    return (
      <div className="space-y-0.5">
        {approvers.map((approver, index) => {
          const prefix = approver.type === 'Reviewer' ? '[R]' : '[A]';
          const dateStr = approver.date
            ? new Date(approver.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : approver.isAssigned ? '(assigned)' : '';
          
          // Get status text and color for display
          //console.log('Approver status debug:', {
          //  name: approver.name,
          //  status: approver.status,
          //  statusType: typeof approver.status
          //});
          
          let statusText, statusColor;
          switch (approver.status) {
            case 'Approved':
              statusText = '(Approved)';
              statusColor = '#16a34a'; // Green-600
              break;
            case 'Rejected':
              statusText = '(Rejected)';
              statusColor = '#dc2626'; // Red-600
              break;
            case 'Pending':
              statusText = '(Pending)';
              statusColor = '#ca8a04'; // Yellow-600
              break;
            case 'Cancelled':
              statusText = '(Cancelled)';
              statusColor = '#6b7280'; // Gray-600
              break;
            case 'Overdue':
              statusText = '(Overdue)';
              statusColor = '#991b1b'; // Red-800
              break;
            default:
              statusText = `(${approver.status?.charAt(0).toUpperCase() + approver.status?.slice(1)})`;
              statusColor = '#6b7280'; // Gray-600
              break;
          }

          return (
            <div key={index} className="text-xs font-medium">
              {prefix} {approver.name} <span style={{color: statusColor, fontWeight: 'bold'}}>{statusText}</span> {dateStr}
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntityDetails = () => {
    if (!entity || !entityData) return null;
    return <ApprovalEntitySummary entityData={entityData} />;
  };

  const renderModalForm = () => (
    <Modal isOpen={showApprovalForm} onClose={() => setShowApprovalForm(false)} size="lg">
      <ModalHeader onClose={() => setShowApprovalForm(false)}>
        <ModalTitle>Request {defaultConfig.title}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        {renderEntityDetails()}
        <ApprovalIntegration
          key={`approval-form-${entity.id}-${showApprovalForm}`}
          entityType={entityType}
          entityId={entity.id}
          entityData={entityData}
          entity={entity}
          document={document}
          onSubmit={onFormSubmit || handleApprovalRequest}
          onCancel={onFormCancel || (() => setShowApprovalForm(false))}
          isLoading={isLoading}
        />
      </ModalContent>
    </Modal>
  );

  const totalLoading = approvalLoading || isLoading;

  // Modal form mode
  if (formMode === 'modal') {
    return (
      <>
        {renderModalForm()}
        {/* Show the main content with button to open modal */}
        <div className="space-y-2">
          {/* Approval Status Card */}
          <Card>
            <CardHeader className="pb-2 px-2 pt-2">
              <div className="space-y-2">
                <CardTitle className="text-xs sm:text-sm flex items-center gap-1 ml-4">
                  <ClockIcon className="h-4 w-4" />
                  {defaultConfig.title}
                </CardTitle>
                <div className="flex justify-start ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowApprovalForm(true)}
                    disabled={totalLoading}
                    className="flex items-center gap-1 text-xs text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white px-2 py-1"
                  >
                    <PlusIcon className="h-3 w-3 flex-shrink-0" />
                    <span className="hidden md:inline">Request Review and Approval</span>
                    <span className="hidden sm:inline md:hidden">Request Review</span>
                    <span className="sm:hidden">Request</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-1 p-2">
              {approvalRequests && approvalRequests.length > 0 ? (
                <div className="space-y-2">
                  {approvalRequests.map((approval) => {
                    const approvers = getApproverInfo(approval);
                    return (
                      <div key={approval.id} className="mb-1 rounded border border-gray-200 bg-white p-1.5 dark:border-gray-700 dark:bg-gray-800/50">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
                          {/* Left side - Status and Info */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            {getStatusText(approval.status)}
                            <div className="text-xs">
                              <span className="font-medium text-gray-900 dark:text-gray-100">#{approval.code}</span>
                              <span className="ml-1 hidden text-gray-500 dark:text-gray-400 sm:inline">
                                {new Date(approval.requestedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} by {approval.requestedBy?.userDisplayName || approval.requestedBy?.userFirstName || 'Unknown'}
                              </span>
                              <span className="ml-1 text-gray-500 dark:text-gray-400 sm:hidden">
                                {new Date(approval.requestedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          </div>

                          {/* Right side - Buttons */}
                          <div className="flex justify-end lg:justify-start gap-2">
                            {/* View button - always available */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleApprovalAction('view', { approvalId: approval.id });
                              }}
                              className="text-xs px-3 py-1.5 text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                              title="View approval request"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                            
                            {/* Delete button - only if no actions by other users */}
                            {canDeleteApproval(approval) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleApprovalAction('delete', { approvalId: approval.id });
                                }}
                                className="text-xs px-3 py-1.5 text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                                title="Delete approval request"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Approver Info - Always at bottom for all screen sizes */}
                        <div className="mt-1">
                          {approvers}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {defaultConfig.noApprovalMessage}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit/View Approval Request Form Modal */}
        {editFormState.show && editFormState.approval && (
          <Modal isOpen={editFormState.show} onClose={() => setEditFormState({ show: false, approval: null, viewOnly: false })} size="lg">
            <ModalHeader>
              <ModalTitle>
                {editFormState.viewOnly ? 'View' : 'Edit'} {defaultConfig.title}
              </ModalTitle>
            </ModalHeader>
            <ModalContent>
              {renderEntityDetails()}
              <ApprovalIntegration
                entityType={entityType}
                entityId={entity.id}
                entityData={entityData}
                entity={entity}
                document={document}
                existingApproval={editFormState.approval}
                onSubmit={editFormState.viewOnly ? undefined : handleEditApprovalRequest}
                onCancel={() => setEditFormState({ show: false, approval: null, viewOnly: false })}
                isLoading={totalLoading}
                isEditMode={!editFormState.viewOnly}
                isViewOnly={editFormState.viewOnly}
            />
          </ModalContent>
        </Modal>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={deleteConfirmState.show}
          onClose={() => setDeleteConfirmState({ show: false, approval: null })}
          onConfirm={confirmDeleteApproval}
          title="Delete Approval Request"
          message={`Are you sure you want to delete approval request "${deleteConfirmState.approval?.code || deleteConfirmState.approval?.title}"?`}
          warningMessage="This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="destructive"
          isLoading={isLoading}
        />

        {/* Alert Dialog */}
        <AlertDialog
          isOpen={alertState.show}
          onClose={() => setAlertState({ show: false, message: '', type: 'error' })}
          title=""
          message={alertState.message}
          type={alertState.type}
          confirmText="OK"
        />
      </>
    );
  }

  // Default standalone mode
  return (
    <div className="space-y-4">
      {/* Minimal Approval Status Panel */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/40">
        {/* Header with Always-Visible Button */}
        <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {defaultConfig.title}
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowApprovalForm(true)}
            disabled={totalLoading}
            className="flex items-center gap-1 text-xs text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white"
          >
            <PlusIcon className="h-3 w-3" />
            {defaultConfig.approvalButtonText}
          </Button>
        </div>

        {/* Approval Requests List */}
        {approvalRequests && approvalRequests.length > 0 ? (
          <div className="space-y-2">
            {approvalRequests.map((approval) => (
              <div key={approval.id} className="rounded border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusText(approval.status)}
                    <span className="text-xs text-gray-600 dark:text-gray-400">#{approval.code}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleApprovalAction('view', { approvalId: approval.id })}
                    className="text-xs"
                  >
                    View
                  </Button>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Requested by {approval.requestedBy?.userDisplayName || 'Unknown'} on{' '}
                  {new Date(approval.requestedAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {defaultConfig.noApprovalMessage}
            </p>
          </div>
        )}
      </div>

      {/* Create Approval Request Form Modal */}
      {showApprovalForm && (
        <Modal isOpen={showApprovalForm} onClose={() => setShowApprovalForm(false)} size="lg">
          <ModalHeader>
            <ModalTitle>Request {defaultConfig.title}</ModalTitle>
          </ModalHeader>
          <ModalContent>
            {renderEntityDetails()}
            <ApprovalIntegration
              key={`approval-form-${entity.id}-${showApprovalForm}`}
              entityType={entityType}
              entityId={entity.id}
              entityData={entityData}
              entity={entity}
              document={document}
              onSubmit={handleApprovalRequest}
              onCancel={() => setShowApprovalForm(false)}
              isLoading={totalLoading}
            />
          </ModalContent>
        </Modal>
      )}

      {/* Edit/View Approval Request Form Modal */}
      {editFormState.show && editFormState.approval && (
        <Modal isOpen={editFormState.show} onClose={() => setEditFormState({ show: false, approval: null, viewOnly: false })} size="lg">
          <ModalHeader>
            <ModalTitle>
              {editFormState.viewOnly ? 'View' : 'Edit'} {defaultConfig.title}
            </ModalTitle>
          </ModalHeader>
          <ModalContent>
            <ApprovalIntegration
              entityType={entityType}
              entityId={entity.id}
              entityData={entityData}
              entity={entity}
              document={document}
              existingApproval={editFormState.approval}
              onSubmit={editFormState.viewOnly ? undefined : handleEditApprovalRequest}
              onCancel={() => setEditFormState({ show: false, approval: null, viewOnly: false })}
              onError={(error) => {
                setAlertState({
                  show: true,
                  message: error.message || 'Failed to update approval request. Please try again.',
                  type: 'error'
                });
              }}
              isLoading={totalLoading}
              isEditMode={!editFormState.viewOnly}
              isViewOnly={editFormState.viewOnly}
            />
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmState.show}
        onClose={() => setDeleteConfirmState({ show: false, approval: null })}
        onConfirm={confirmDeleteApproval}
        title="Delete Approval Request"
        message={`Are you sure you want to delete approval request "${deleteConfirmState.approval?.code || deleteConfirmState.approval?.title}"?`}
        warningMessage="This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="destructive"
        isLoading={isLoading}
      />

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertState.show}
        onClose={() => setAlertState({ show: false, message: '', type: 'error' })}
        title=""
        message={alertState.message}
        type={alertState.type}
        confirmText="OK"
      />
    </div>
  );
};

// Export the component
export default GenericApprovalIntegration;